import type {Alpine as AlpineType} from 'alpinejs';

const THEME_VALUES = ['light', 'dark', 'system'] as const;
type ThemeMode = typeof THEME_VALUES[number];
type ResolvedThemeMode = Exclude<ThemeMode, 'system'>;

type ThemeChangeDetail = {
    mode: ThemeMode;
    resolved: ResolvedThemeMode;
    dark: boolean;
    on: boolean;
};

type ThemeStore = {
    mode: ThemeMode;
    resolved: ResolvedThemeMode;
    dark: boolean;
    on: boolean;
    set: (mode: ThemeMode) => void;
    toggle: () => void;
};

const isThemeMode = (value: unknown): value is ThemeMode => (
    typeof value === 'string' && THEME_VALUES.includes(value as ThemeMode)
);

const normalizeThemeMode = (value: unknown): ThemeMode => (
    isThemeMode(value) ? value : 'system'
);

const storageKey = (): string => (
    document.documentElement.dataset.themeStorageKey || 'trellis-ui-theme'
);

const fallbackMode = (): ThemeMode => normalizeThemeMode(document.documentElement.dataset.theme);

const storedMode = (): ThemeMode => {
    try {
        return normalizeThemeMode(window.localStorage.getItem(storageKey()) || fallbackMode());
    } catch {
        return fallbackMode();
    }
};

const persistMode = (mode: ThemeMode): void => {
    try {
        window.localStorage.setItem(storageKey(), mode);
    } catch {
        // Keep theme controls usable in private or restricted browsing contexts.
    }
};

const prefersDark = (): boolean => (
    window.matchMedia('(prefers-color-scheme: dark)').matches
);

const resolveDark = (mode: ThemeMode): boolean => (
    mode === 'dark' || (mode === 'system' && prefersDark())
);

const applyThemeMode = (mode: ThemeMode): ThemeChangeDetail => {
    const dark = resolveDark(mode);
    const resolved: ResolvedThemeMode = dark ? 'dark' : 'light';
    const root = document.documentElement;

    root.classList.toggle('dark', dark);
    root.dataset.theme = mode;
    root.dataset.resolvedTheme = resolved;
    root.dispatchEvent(new CustomEvent<ThemeChangeDetail>('ui:theme-change', {
        detail: {mode, resolved, dark, on: dark},
        bubbles: true,
    }));

    return {mode, resolved, dark, on: dark};
};

const createThemeStore = (): ThemeStore => {
    const initial = applyThemeMode(storedMode());

    return {
        mode: initial.mode,
        resolved: initial.resolved,
        dark: initial.dark,
        on: initial.on,
        set(mode: ThemeMode): void {
            const nextMode = normalizeThemeMode(mode);
            persistMode(nextMode);
            const next = applyThemeMode(nextMode);
            this.mode = next.mode;
            this.resolved = next.resolved;
            this.dark = next.dark;
            this.on = next.on;
        },
        toggle(): void {
            this.set(this.dark ? 'light' : 'dark');
        },
    };
};

const registerThemeStore = (AlpineInstance: AlpineType): void => {
    if (typeof AlpineInstance.store !== 'function') return;

    const existingStore = AlpineInstance.store('theme') as ThemeStore | undefined;
    const store = existingStore ?? createThemeStore();

    AlpineInstance.store('theme', store);

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (store.mode !== 'system') return;
        const next = applyThemeMode('system');
        store.mode = next.mode;
        store.resolved = next.resolved;
        store.dark = next.dark;
        store.on = next.on;
    });
};

export {
    applyThemeMode,
    createThemeStore,
    registerThemeStore,
    type ResolvedThemeMode,
    type ThemeChangeDetail,
    type ThemeStore,
    type ThemeMode,
};
